import { useState, useEffect, useMemo } from 'react';
import AlertBanner from './components/AlertBanner';
import StatusCards from './components/StatusCards';
import UploadStatus from './components/UploadStatus';
import Filters from './components/Filters';
import OrdersTable, { shipToLines } from './components/OrdersTable';
import UploadModal from './components/UploadModal';
import GreenlightModal from './components/GreenlightModal';
import SecretKeyModal from './components/SecretKeyModal';
import BatchExports from './components/BatchExports';
import MspModal from './components/MspModal';
import ReorderModal from './components/ReorderModal';
import BackbookModal from './components/BackbookModal';
import BackbookNewStandsModal from './components/BackbookNewStandsModal';
import BatchDownloadModal from './components/BatchDownloadModal';
import ProcessingHub from './components/ProcessingHub';
import PendingEmailBanner from './components/PendingEmailBanner';
import QrPendingBanner from './components/QrPendingBanner';
import TaxInvoicesPanel from './components/TaxInvoicesPanel';
import ToolHealthPanel from './components/ToolHealthPanel';

function App() {
  const [orders, setOrders] = useState([]);
  const [flagged, setFlagged] = useState([]);
  const [latestUpload, setLatestUpload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [batchFilter, setBatchFilter] = useState('');
  const [activeCard, setActiveCard] = useState(null);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [showUpload, setShowUpload] = useState(false);
  const [showGreenlight, setShowGreenlight] = useState(false);
  const [showMsp, setShowMsp] = useState(false);
  const [showReorder, setShowReorder] = useState(false);
  const [showBackbook, setShowBackbook] = useState(false);
  const [showBackbookNewStands, setShowBackbookNewStands] = useState(false);
  const [mspPending, setMspPending] = useState(null);
  const [uploadHistory, setUploadHistory] = useState([]);
  const [greenlightRefresh, setGreenlightRefresh] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastSyncResult, setLastSyncResult] = useState(null);
  const [editSecretKey, setEditSecretKey] = useState('');
  const [showSecretModal, setShowSecretModal] = useState(false);
  const [secretError, setSecretError] = useState(null);
  // ?batch=LABEL deep link from the confirmation email — read once on mount so
  // closing the modal doesn't reopen it
  const [linkedBatch, setLinkedBatch] = useState(
    () => new URLSearchParams(window.location.search).get('batch') || null
  );

  const fetchData = () => {
    return Promise.all([
      fetch('/api/orders').then((r) => r.json()),
      fetch('/api/orders/flagged').then((r) => r.json()),
      fetch('/api/uploads/latest').then((r) => r.json()),
      fetch('/api/uploads/history').then((r) => r.json()),
      fetch('/api/msp/pending').then((r) => r.json()),
    ])
      .then(([ordersData, flaggedData, uploadData, historyData, mspData]) => {
        setOrders(ordersData);
        setFlagged(flaggedData);
        setLatestUpload(uploadData);
        setUploadHistory(historyData);
        setMspPending(mspData && !mspData.error ? mspData : null);
      })
      .catch((err) => console.error('Failed to load data:', err));
  };

  useEffect(() => {
    fetchData().finally(() => setLoading(false));
  }, []);

  const flaggedIds = useMemo(() => new Set(flagged.map((o) => o.id)), [flagged]);

  const mseEligible = useMemo(
    () => orders.filter((o) => o.source === 'MSE' && o.status === 'codes_completed').length,
    [orders]
  );

  const statuses = useMemo(() => {
    const s = new Set(orders.map((o) => o.status).filter(Boolean));
    return [...s].sort();
  }, [orders]);

  const batches = useMemo(() => {
    const b = new Set(orders.map((o) => o.batch_production).filter(Boolean));
    return [...b].sort();
  }, [orders]);

  const today = useMemo(() => new Date().toDateString(), []);

  const filteredOrders = useMemo(() => {
    return orders.filter((order) => {
      // Status dropdown filter
      if (statusFilter && order.status !== statusFilter) return false;

      // Batch dropdown filter
      if (batchFilter && order.batch_production !== batchFilter) return false;

      // Search filter
      if (search) {
        const q = search.toLowerCase();
        const match =
          (order.order_uid || '').toLowerCase().includes(q) ||
          (order.client || '').toLowerCase().includes(q) ||
          (order.tracking || '').toLowerCase().includes(q) ||
          (order.notes || '').toLowerCase().includes(q) ||
          shipToLines(order).full.toLowerCase().includes(q);
        if (!match) return false;
      }

      // Card filter
      if (activeCard === 'needsAttention') {
        return flaggedIds.has(order.id);
      }
      if (activeCard === 'inTransit') {
        const s = (order.status || '').toLowerCase();
        return s === 'shipped' || s === 'in_transit';
      }
      if (activeCard === 'deliveredToday') {
        return (
          (order.status || '').toLowerCase() === 'delivered' &&
          order.delivered_at &&
          new Date(order.delivered_at).toDateString() === today
        );
      }
      if (activeCard === 'awaitingShipment') {
        return order.send_authorized && !order.tracking;
      }

      return true;
    });
  }, [orders, search, statusFilter, batchFilter, activeCard, flaggedIds, today]);

  // Reset to page 1 whenever the filtered set changes shape
  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, batchFilter, activeCard, perPage]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / perPage));
  const currentPage = Math.min(page, totalPages);
  const pagedOrders = useMemo(
    () => filteredOrders.slice((currentPage - 1) * perPage, currentPage * perPage),
    [filteredOrders, currentPage, perPage]
  );

  const handleSync = async () => {
    setSyncing(true);
    setLastSyncResult(null);
    try {
      const res = await fetch('/api/sync/run', { method: 'POST' });
      const data = await res.json();
      setLastSyncResult(data);
      await fetchData();
    } catch (err) {
      alert('Sync failed: ' + err.message);
    } finally {
      setSyncing(false);
    }
  };

  const handleNeedAuth = () => {
    setSecretError(null);
    setShowSecretModal(true);
  };

  const handleSecretSubmit = (key) => {
    setEditSecretKey(key);
    setShowSecretModal(false);
  };

  const handleAuthFailed = () => {
    setEditSecretKey('');
    setSecretError('Invalid secret key. Please re-enter.');
    setShowSecretModal(true);
  };

  const handleCardClick = (cardKey) => {
    setActiveCard(activeCard === cardKey ? null : cardKey);
  };

  // Emailed links land here — show the downloads immediately rather than
  // making production wait on the dashboard's order queries
  const batchLinkModal = linkedBatch && (
    <BatchDownloadModal
      label={linkedBatch}
      onClose={() => {
        setLinkedBatch(null);
        // Drop the param so a refresh doesn't reopen the modal
        window.history.replaceState({}, '', window.location.pathname);
      }}
    />
  );

  // Both branches return [div, modal] so the modal keeps its slot when
  // loading flips — otherwise it remounts and re-fetches its manifest
  if (loading) {
    return (
      <>
        <div className="min-h-screen bg-[#f8f9fb] flex items-center justify-center">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-[3px] border-gray-200 border-t-indigo-600 rounded-full animate-spin" />
            <p className="text-sm text-gray-400 tracking-wide">Loading dashboard...</p>
          </div>
        </div>
        {batchLinkModal}
      </>
    );
  }

  return (
    <>
    <div className="min-h-screen bg-[#f8f9fb]">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              </div>
              <h1 className="text-lg font-semibold text-gray-900 tracking-tight">Shipment Tracking</h1>
            </div>
            <div className="flex items-center gap-4">
              <UploadStatus latestUpload={latestUpload} />
              <span className="text-xs text-gray-400">
                {orders.length} total orders
              </span>
              <button
                onClick={handleSync}
                disabled={syncing}
                className="px-3.5 py-1.5 bg-white border border-gray-200 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 transition-colors flex items-center gap-1.5"
              >
                <svg className={`w-3.5 h-3.5 ${syncing ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182" />
                </svg>
                {syncing ? 'Syncing...' : 'Sync FedEx'}
              </button>
              <button
                onClick={() => setShowUpload(true)}
                className="px-3.5 py-1.5 bg-indigo-600 text-white text-xs font-medium rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1.5"
              >
                <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
                Upload CSV
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-8">
        {/* Batches processed without their confirmation email — must not be missed */}
        <PendingEmailBanner
          refreshKey={greenlightRefresh}
          secretKey={editSecretKey}
          onNeedAuth={handleNeedAuth}
          onAuthFailed={handleAuthFailed}
        />

        {/* QR art generation stalled in the orders tool — nothing below can process those orders */}
        <QrPendingBanner
          secretKey={editSecretKey}
          onNeedAuth={handleNeedAuth}
          onAuthFailed={handleAuthFailed}
          onFixed={fetchData}
        />

        {/* Slim don't-miss strip for MSP — the hub card below is the real entry point */}
        {mspPending && mspPending.counts.total > 0 && (
          <div className="bg-indigo-600 rounded-xl px-5 py-2.5 mb-6 flex items-center justify-between shadow-sm">
            <p className="text-xs text-white font-semibold">
              {mspPending.counts.total} MSP store order{mspPending.counts.total !== 1 ? 's' : ''} awaiting processing — don't forget these
            </p>
            <button
              onClick={() => setShowMsp(true)}
              className="px-3 py-1 bg-white text-indigo-700 text-xs font-semibold rounded-lg hover:bg-indigo-50 transition-colors"
            >
              Process
            </button>
          </div>
        )}

        {/* Standing entry points for the four order types */}
        <ProcessingHub
          mseEligible={mseEligible}
          mspCounts={mspPending?.counts}
          onOpenGreenlight={() => setShowGreenlight(true)}
          onOpenMsp={() => setShowMsp(true)}
          onOpenReorder={() => setShowReorder(true)}
          onOpenBackbook={() => setShowBackbook(true)}
          onOpenBackbookNewStands={() => setShowBackbookNewStands(true)}
        />

        {/* What the orders tool has logged as errors, explained — fetches on its own */}
        <ToolHealthPanel />

        {/* Stale upload warning */}
        {(() => {
          const isStale = latestUpload
            ? Math.floor((new Date() - new Date(latestUpload.uploaded_at)) / (1000 * 60 * 60 * 24)) >= 7
            : true;
          const daysSince = latestUpload
            ? Math.floor((new Date() - new Date(latestUpload.uploaded_at)) / (1000 * 60 * 60 * 24))
            : null;
          if (!isStale) return null;
          return (
            <div className="bg-red-50 border border-red-200 rounded-xl px-5 py-3.5 mb-6 flex items-center justify-between">
              <div className="flex items-center gap-2.5">
                <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
                  </svg>
                </div>
                <p className="text-sm text-red-900 font-medium">
                  {latestUpload
                    ? `ShipRush export is overdue — last upload was ${daysSince} days ago (${latestUpload.filename})`
                    : 'No ShipRush export has been uploaded yet'}
                </p>
              </div>
              <button
                onClick={() => setShowUpload(true)}
                className="px-4 py-1.5 bg-red-600 text-white text-xs font-medium rounded-lg hover:bg-red-700 transition-colors"
              >
                Upload Now
              </button>
            </div>
          );
        })()}

        {/* Sync result */}
        {lastSyncResult && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-5 py-3.5 mb-6 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center flex-shrink-0">
                <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <p className="text-sm text-emerald-900 font-medium">
                FedEx sync complete — checked {lastSyncResult.synced} orders, {lastSyncResult.delivered} marked delivered
                {lastSyncResult.errors > 0 && `, ${lastSyncResult.errors} errors`}
              </p>
            </div>
            <button
              onClick={() => setLastSyncResult(null)}
              className="text-emerald-600 hover:text-emerald-800 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        <AlertBanner
          flaggedOrders={flagged}
          secretKey={editSecretKey}
          onNeedAuth={handleNeedAuth}
          onAuthFailed={handleAuthFailed}
          onOrderUpdated={fetchData}
        />
        <StatusCards
          orders={orders}
          flaggedCount={flagged.length}
          activeCard={activeCard}
          onCardClick={handleCardClick}
        />

        {/* Table section */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="px-5 py-4 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h2 className="text-sm font-semibold text-gray-900">
                  {activeCard ? 'Filtered Orders' : 'All Orders'}
                </h2>
                {activeCard && (
                  <button
                    onClick={() => setActiveCard(null)}
                    className="text-xs text-indigo-600 hover:text-indigo-800 font-medium"
                  >
                    Clear filter
                  </button>
                )}
              </div>
              <span className="text-xs text-gray-400">
                {filteredOrders.length === orders.length
                  ? `${orders.length} orders`
                  : `${filteredOrders.length} of ${orders.length}`}
              </span>
            </div>
            <div className="mt-3">
              <Filters
                search={search}
                onSearchChange={setSearch}
                statusFilter={statusFilter}
                onStatusChange={setStatusFilter}
                statuses={statuses}
                batchFilter={batchFilter}
                onBatchChange={setBatchFilter}
                batches={batches}
              />
            </div>
          </div>
          <OrdersTable
            orders={pagedOrders}
            flaggedIds={flaggedIds}
            onOrderUpdated={fetchData}
            secretKey={editSecretKey}
            onNeedAuth={handleNeedAuth}
            onAuthFailed={handleAuthFailed}
          />

          {/* Pagination */}
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="text-xs text-gray-400 tabular-nums">
                {filteredOrders.length === 0
                  ? 'No orders'
                  : `Showing ${(currentPage - 1) * perPage + 1}–${Math.min(currentPage * perPage, filteredOrders.length)} of ${filteredOrders.length}`}
              </span>
              <select
                value={perPage}
                onChange={(e) => setPerPage(Number(e.target.value))}
                className="text-xs border border-gray-200 rounded-lg px-2 py-1 bg-white text-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
              >
                <option value={25}>25 / page</option>
                <option value={50}>50 / page</option>
                <option value={100}>100 / page</option>
              </select>
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => setPage(1)}
                disabled={currentPage === 1}
                className="px-2.5 py-1 text-xs border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                First
              </button>
              <button
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-2.5 py-1 text-xs border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Prev
              </button>
              <span className="text-xs text-gray-500 px-2 tabular-nums">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-2.5 py-1 text-xs border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Next
              </button>
              <button
                onClick={() => setPage(totalPages)}
                disabled={currentPage === totalPages}
                className="px-2.5 py-1 text-xs border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Last
              </button>
            </div>
          </div>
        </div>

        {/* Upload History */}
        {uploadHistory.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm mt-6">
            <div className="px-5 py-4 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">Upload History</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100">
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Filename</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Uploaded</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Rows</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Matched</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Staged</th>
                    <th className="text-left px-5 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Applied</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadHistory.map((u, idx) => (
                    <tr key={u.id} className={`${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'} border-b border-gray-100/70`}>
                      <td className="px-5 py-3 font-mono text-xs font-medium text-gray-900">{u.filename}</td>
                      <td className="px-5 py-3 text-gray-500">
                        {new Date(u.uploaded_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        <span className="text-gray-400 ml-1">
                          {new Date(u.uploaded_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-gray-600 tabular-nums">{u.row_count}</td>
                      <td className="px-5 py-3 text-gray-600 tabular-nums">{u.records_matched}</td>
                      <td className="px-5 py-3 text-gray-600 tabular-nums">{u.records_created}</td>
                      <td className="px-5 py-3">
                        {u.applied_at ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-medium">
                            <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                            </svg>
                            {new Date(u.applied_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        ) : (
                          <span className="text-xs text-amber-600 font-medium">Pending</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        <BatchExports refreshKey={greenlightRefresh} />

        {/* Hidden until the edit key is entered — see TaxInvoicesPanel */}
        <TaxInvoicesPanel
          refreshKey={greenlightRefresh}
          secretKey={editSecretKey}
          onNeedAuth={handleNeedAuth}
          onAuthFailed={handleAuthFailed}
          onSecretVerified={setEditSecretKey}
        />
      </main>

      {showUpload && (
        <UploadModal
          onClose={() => setShowUpload(false)}
          onUploadComplete={fetchData}
        />
      )}

      {showGreenlight && (
        <GreenlightModal
          onClose={() => setShowGreenlight(false)}
          onComplete={() => {
            fetchData();
            setGreenlightRefresh((n) => n + 1);
          }}
          secretKey={editSecretKey}
          onNeedAuth={handleNeedAuth}
          onAuthFailed={handleAuthFailed}
        />
      )}

      {showMsp && (
        <MspModal
          onClose={() => setShowMsp(false)}
          onComplete={() => {
            fetchData();
            setGreenlightRefresh((n) => n + 1);
          }}
          secretKey={editSecretKey}
          onNeedAuth={handleNeedAuth}
          onAuthFailed={handleAuthFailed}
        />
      )}

      {showReorder && (
        <ReorderModal
          onClose={() => setShowReorder(false)}
          onComplete={() => {
            fetchData();
            setGreenlightRefresh((n) => n + 1);
          }}
          secretKey={editSecretKey}
          onNeedAuth={handleNeedAuth}
          onAuthFailed={handleAuthFailed}
        />
      )}

      {showBackbook && (
        <BackbookModal
          onClose={() => setShowBackbook(false)}
          onComplete={() => {
            fetchData();
            setGreenlightRefresh((n) => n + 1);
          }}
          secretKey={editSecretKey}
          onNeedAuth={handleNeedAuth}
          onAuthFailed={handleAuthFailed}
        />
      )}

      {showBackbookNewStands && (
        <BackbookNewStandsModal
          onClose={() => setShowBackbookNewStands(false)}
          onComplete={() => {
            fetchData();
            setGreenlightRefresh((n) => n + 1);
          }}
          secretKey={editSecretKey}
          onNeedAuth={handleNeedAuth}
          onAuthFailed={handleAuthFailed}
        />
      )}

      {showSecretModal && (
        <SecretKeyModal
          onSubmit={handleSecretSubmit}
          onCancel={() => { setShowSecretModal(false); setSecretError(null); }}
          error={secretError}
        />
      )}
    </div>
    {batchLinkModal}
    </>
  );
}

export default App;
